import type {Thread} from './conversation-types'
import type {PatchStatus} from '@github-ui/diff-file-helpers'
import type {DiffLineType} from '@github-ui/diffs/types'
import type {FileRendererBlobData} from '@github-ui/file-renderer-blob'
import type {SafeHTMLString} from '@github-ui/safe-html'
import type {CommentsPreference, DiffLineSpacingPreference, SplitPreference} from '@github-ui/diff-view-settings/types'
import {Type} from '@github-ui/react-query-persister/typebox'
import {Compile} from '@github-ui/react-query-persister/typebox/compile'

export type {FileDiffReference} from '@github-ui/copilot-chat-types'

export interface CopilotDiffContext {
  commitMessage?: string
  pullRequestUrl?: string
}

type TreeEntry = {
  readonly lineCount: number | null | undefined
  readonly mode: number
  readonly path?: string | null | undefined
}

export type NewTreeEntry = TreeEntry & {
  readonly isGenerated: boolean
}

export type OldTreeEntry = TreeEntry

export type DiffEntryData = {
  readonly diffLines: DiffLine[]
  readonly isBinary: boolean
  readonly isTooBig: boolean
  readonly linesChanged: number
  readonly newTreeEntry: NewTreeEntry | null | undefined
  readonly newCommitOid?: string
  readonly objectId: string
  readonly oldTreeEntry: OldTreeEntry | null | undefined
  readonly oldCommitOid?: string
  readonly path: string
  readonly pathDigest: string
  readonly status: PatchStatus
  readonly truncatedReason: string | null | undefined
}

export type DiffLineKey = `b:${number}-l:${number | null}-r:${number | null}`

// Unfortunately the typebox schema precompiler doesn't support regex literals so we have to use a string regex
const DIFF_LINE_KEY_PATTERN = '^b:\\d+-l:(?:\\d+|null)-r:(?:\\d+|null)$'

export const DiffLineKeySchema = Type.Unsafe<DiffLineKey>(Type.String({pattern: DIFF_LINE_KEY_PATTERN}))
export const DiffLineKeyValidator = Compile(DiffLineKeySchema)

export type DiffLine = {
  readonly __id?: string
  readonly left: number | null
  readonly right: number | null
  readonly blobLineNumber: number
  readonly displayNoNewLineWarning?: boolean
  readonly position?: number | null
  readonly type: DiffLineType
  readonly html: string
  readonly text: string
  readonly key?: DiffLineKey | undefined
  hasHiddenUnicode?: boolean | undefined | null
}

// The number represents the blob line number in the diff
export type LineKey = `L${number}` | `R${number}`
export type FileKey = `F${number}`
// Due to typescript weirdness, we can't use 'FILE' as a LineKey without breaking the type declaration in markers.ts
// Since the server returns 'FILE' as the line key for file-level comments, I kept things consistent by using this
// constant. In the future, when we make server side updates, we should consider changing the server side file key
// to be 'F0' or something similar to match the LineKey type declaration.
export const fileLevelCommentKey = 'FILE' as FileKey

export type DiffLineSelectionInfo = {
  isSelected?: boolean
  isLastSelected?: boolean
  isFirstSelected?: boolean
}

export type DiffLineWithSelectionInfo = DiffLine & DiffLineSelectionInfo

export type DiffOrientation = 'left' | 'right'

export function isDiffOrientation(value: string | null | undefined): value is DiffOrientation {
  return value === 'left' || value === 'right'
}

export interface DiffRange {
  baseOid: string
  headOid: string
}

export type ThreadsData = {
  readonly __id?: string
  readonly totalCommentsCount: number
  readonly totalCount: number
  threads: Thread[]
}

export type Comments = {
  readonly __id?: string
  readonly totalCount: number
  readonly comments: ReadonlyArray<Comment | null> | null
}

export type Comment = {
  readonly id?: number
  readonly author: CommentAuthor | null
}

export type CommentAuthor = {
  avatarUrl: string
  login: string
  url: string
}

export const EMPTY_DIFF_LINE = 'empty-diff-line'
export type EmptyDiffLine = 'empty-diff-line'

export type ClientDiffLine = DiffLineWithSelectionInfo | EmptyDiffLine

export type DiffSide = 'LEFT' | 'RIGHT'

export type RichDiff = {
  readonly canToggleRichDiff: boolean
  readonly defaultToRichDiff: boolean
  readonly proseDiffHtml?: SafeHTMLString
  readonly renderInfo?: FileRendererBlobData
  readonly dependencyDiffPath?: string
}

export type SubmoduleDiff = {
  /** The file path of the submodule in the current repo */
  basePath: string
  /**
   * The url to the submodule root
   *
   * This can be a repo, gist, wiki, or non-GitHub URL
   */
  submoduleUrl?: string
  /**
   * The url to the contents of the submodule if it is GH-hosted
   * This is used for linkable sha changes or to build compare URLs for submodule files when the diff summary is available
   *
   * This can be a GH repo, gist, wiki, or tree URL
   */
  contentsUrl?: string
  changedFiles?: number
  oldCommitOid?: string
  newCommitOid?: string
  status: PatchStatus
  summary: SummaryDelta[]
}

export type SummaryDelta = {
  linesAdded: number
  linesDeleted: number
  path: string
  pathDigest: string
  status: PatchStatus
}

// eslint-disable-next-line unused-imports/no-unused-vars
const STATE_LABEL_DATA = {
  OPEN: {
    description: 'Open',
    status: 'pullOpened',
  },
  CLOSED: {
    description: 'Closed',
    status: 'pullClosed',
  },
  QUEUED: {
    description: 'Queued',
    status: 'pullQueued',
  },
  MERGED: {
    description: 'Merged',
    status: 'pullMerged',
  },
  DRAFT: {
    description: 'Draft',
    status: 'draft',
  },
}

export type PullRequestState = keyof typeof STATE_LABEL_DATA

// The subject of the comment
// e.g. PullRequest or a Commit
export type Subject = {
  isInMergeQueue?: boolean
  state?: string
}

export type DiffEntry = DiffEntryData & {
  isSubmodule?: boolean
  richDiff?: RichDiff
  submodule?: SubmoduleDiff
  helpUrl: string
  diffSize: string
  linesAdded: number
  linesDeleted: number
  changeType: string
  collapsed?: boolean
  reviewed?: boolean
}

export type DiffUser = {
  avatarURL: string
  canComment: boolean
  canApplySuggestion: boolean
  commentsPreference: CommentsPreference
  hasCopilotAccess: boolean
  lineSpacing: DiffLineSpacingPreference
  login: string
  shouldDisplayComments: boolean
  shouldDisplayEditOptions: boolean
  splitPreference: SplitPreference
  tabSize: number | undefined
}

export const CommentSubjectType = {
  Line: 'line',
  Multiline: 'multiline',
  File: 'file',
} as const

export type CommentSubjectType = (typeof CommentSubjectType)[keyof typeof CommentSubjectType]

export interface LineCommentPosition {
  baseCommitOid: string
  headCommitOid: string
  type: Extract<CommentSubjectType, 'line'>
  line: number
  path: string
  commitOid: string
}

export interface MultilineCommentPosition {
  baseCommitOid: string
  headCommitOid: string
  type: Extract<CommentSubjectType, 'multiline'>
  startPath: string
  startLine: number
  startCommitOid: string
  endPath: string
  endLine: number
  endCommitOid: string
}

export interface FileCommentPosition {
  baseCommitOid: string
  headCommitOid: string
  type: Extract<CommentSubjectType, 'file'>
  path: string
  commitOid: string
}
